import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export interface TabItem {
  id: string;
  label: string;
  active?: boolean;
}

/**
 * TabHeaderComponent renders a horizontal tab header from a list of TabItem objects, manages which tab is active, and emits events when the active tab changes.
 * This component accepts a list of tabs, an optional initial active tab id, and an optional CSS class to customize styling. It sets the first tab as active when none is provided, updates the active tab on user interaction, and emits the selected tab id via the tabChange output.
 * Key methods:
 *   - ngOnInit(): lifecycle hook that ensures a default active tab is selected if none is provided.
 *   - onTabClick(tabId: string): handles user clicks, updates active state and emits tabChange when the tab changes.
 *   - isTabActive(tabId: string): returns true when the provided tabId matches the current activeTabId.
 * Lifecycle hooks:
 *   - OnInit: used to initialize activeTabId to the first tab's id when not specified.
 * @component
 * @selector app-tab-header
 * @example
 *   <app-tab-header [tabs]="sampleTabs" [activeTabId]="initialId" [customClass]="'my-tabs'" (tabChange)="onTabChange($event)"></app-tab-header>
 * @input {TabItem[]} tabs - Array of TabItem objects to display in the header (each item expected to include an id and label).
 * @input {string} activeTabId - Optional id of the tab that should be active initially; if omitted, the first tab will be made active.
 * @input {string} [customClass] - Optional CSS class to apply to the host element for custom styling.
 * @output {EventEmitter<string>} tabChange - Emits the id of the newly selected tab whenever the active tab changes.
 */
@Component({
  selector: 'app-tab-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tab-header.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./tab-header.component.scss'],
})
export class TabHeaderComponent implements OnInit {
  @Input() tabs: TabItem[] = [];
  @Input() activeTabId = '';
  @Input() customClass?: string;

  @Output() tabChange = new EventEmitter<string>();

  ngOnInit(): void {
    // Set first tab as active if no active tab is specified
    if (!this.activeTabId && this.tabs.length > 0) {
      this.activeTabId = this.tabs[0].id;
    }
  }

  onTabClick(tabId: string): void {
    if (tabId !== this.activeTabId) {
      this.activeTabId = tabId;
      this.tabChange.emit(tabId);
    }
  }

  isTabActive(tabId: string): boolean {
    return this.activeTabId === tabId;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
